import React from "react";
import styled from "styled-components";
import { designTokens } from "../../../../components/ui/foundation/DesignTokens";

// ============================================
// Generic widget layout primitives
// ============================================

export const WidgetSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${designTokens.spacing.xl};
  margin-bottom: ${designTokens.spacing.xl};
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns || 4}, 1fr);
  gap: ${designTokens.spacing.lg};

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

// Enhanced MetricCard with subtle left-border accent on hover
export const MetricCard = styled.div`
  background: white;
  border: 1px solid ${designTokens.colors.gray[200]};
  border-radius: ${designTokens.radius.lg};
  box-shadow: ${designTokens.shadows.sm};
  padding: ${designTokens.spacing.xl};
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${(props) => props.accentColor || designTokens.colors.primary[400]};
    border-radius: 3px 0 0 3px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    box-shadow: ${designTokens.shadows.md};
    border-color: ${designTokens.colors.gray[300]};
    transform: translateY(-2px);

    &::before {
      opacity: 1;
    }
  }
`;

export const MetricCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${designTokens.spacing.md};
`;

export const MetricLabel = styled.p`
  font-family: ${designTokens.fonts.body};
  font-size: 0.78rem;
  font-weight: 600;
  color: ${designTokens.colors.gray[500]};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const MetricIconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${designTokens.radius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.bg || designTokens.colors.primary[50]};
  color: ${(props) => props.color || designTokens.colors.primary[500]};
  flex-shrink: 0;

  svg { width: 18px; height: 18px; }
`;

export const MetricValue = styled.div`
  font-family: ${designTokens.fonts.heading};
  font-size: 1.75rem;
  font-weight: 800;
  color: ${designTokens.colors.gray[900]};
  line-height: 1.1;
  margin-bottom: 4px;
`;

export const MetricTrend = styled.p`
  font-size: 0.75rem;
  font-family: ${designTokens.fonts.body};
  margin: 6px 0 0;
  display: flex;
  align-items: center;
  gap: 3px;
  color: ${(props) => (props.negative ? "#DC2626" : "#16A34A")};
  font-weight: 500;
`;

export const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns || 2}, 1fr);
  gap: ${designTokens.spacing.lg};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

// Enhanced ChartCard with a slightly elevated look
export const ChartCard = styled.div`
  background: white;
  border: 1px solid ${designTokens.colors.gray[200]};
  border-radius: ${designTokens.radius.lg};
  box-shadow: ${designTokens.shadows.sm};
  padding: ${designTokens.spacing.xl};
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: ${designTokens.shadows.md};
  }
`;

export const ChartCardTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};
  font-family: ${designTokens.fonts.heading};
  font-size: 0.95rem;
  font-weight: 700;
  color: ${designTokens.colors.gray[800]};
  margin: 0 0 ${designTokens.spacing.lg} 0;
  padding-bottom: ${designTokens.spacing.md};
  border-bottom: 1px solid ${designTokens.colors.gray[100]};

  svg { width: 16px; height: 16px; color: ${designTokens.colors.primary[500]}; flex-shrink: 0; }
`;

export const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, ${designTokens.colors.gray[200]} 25%, ${designTokens.colors.gray[100]} 50%, ${designTokens.colors.gray[200]} 75%);
  background-size: 200% 100%;
  border-radius: ${designTokens.radius.md};
  animation: analytics-shimmer 1.5s ease-in-out infinite;

  @keyframes analytics-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

export const WidgetTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${designTokens.fonts.body};
  font-size: 0.85rem;

  th {
    text-align: left;
    padding: 10px 16px;
    border-bottom: 2px solid ${designTokens.colors.gray[200]};
    color: ${designTokens.colors.gray[500]};
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: ${designTokens.colors.gray[50]};
  }

  td {
    padding: 12px 16px;
    border-bottom: 1px solid ${designTokens.colors.gray[100]};
    color: ${designTokens.colors.gray[800]};
    vertical-align: middle;
  }

  th.align-right, td.align-right { text-align: right; }
  th.align-center, td.align-center { text-align: center; }

  tbody tr {
    transition: background 0.1s ease;
  }

  tbody tr:hover { background: ${designTokens.colors.gray[50]}; }
  tbody tr:last-child td { border-bottom: none; }
`;

export const TableScroll = styled.div`
  overflow-x: auto;
`;

export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.md};
  flex-wrap: wrap;
`;

export const InfoNote = styled.div`
  background: ${designTokens.colors.primary[50]};
  border: 1px solid ${designTokens.colors.primary[200]};
  border-left: 3px solid ${designTokens.colors.primary[400]};
  border-radius: ${designTokens.radius.md};
  padding: ${designTokens.spacing.md} ${designTokens.spacing.lg};
  color: ${designTokens.colors.primary[700]};
  font-family: ${designTokens.fonts.body};
  font-size: 0.8rem;
  line-height: 1.5;
`;

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: ${designTokens.radius.full};
  font-size: 0.72rem;
  font-weight: 700;
  font-family: ${designTokens.fonts.body};
  background: ${(props) => (props.variant === "good" ? "#DCFCE7" : props.variant === "bad" ? "#FEE2E2" : "#FEF3C7")};
  color: ${(props) => (props.variant === "good" ? "#166534" : props.variant === "bad" ? "#991B1B" : "#92400E")};
`;

export const SubTabsBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 2px solid ${designTokens.colors.gray[200]};
  margin-bottom: ${designTokens.spacing.lg};
  flex-wrap: wrap;
`;

export const SubTabButton = styled.button`
  border: none;
  background: transparent;
  padding: 10px 16px;
  font-family: ${designTokens.fonts.body};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(props) => (props.active ? designTokens.colors.primary[600] : designTokens.colors.gray[500])};
  border-bottom: 2px solid ${(props) => (props.active ? designTokens.colors.primary[500] : "transparent")};
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  border-radius: ${designTokens.radius.sm} ${designTokens.radius.sm} 0 0;

  &:hover {
    color: ${designTokens.colors.primary[600]};
    background: ${designTokens.colors.gray[50]};
  }
`;

export const EmptyStateBox = styled.div`
  padding: 56px 24px;
  text-align: center;
  color: ${designTokens.colors.gray[400]};
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

// ============================================
// Section divider with icon + title + description
// ============================================

export const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px solid ${designTokens.colors.gray[100]};
`;

export const SectionDividerIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: ${designTokens.radius.md};
  background: ${(props) => props.bg || designTokens.colors.primary[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;

  svg { width: 17px; height: 17px; }
`;

export const SectionDividerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SectionDividerTitle = styled.h2`
  margin: 0;
  font-family: ${designTokens.fonts.heading};
  font-size: 0.95rem;
  font-weight: 700;
  color: ${designTokens.colors.gray[800]};
  line-height: 1.2;
`;

export const SectionDividerDesc = styled.p`
  margin: 0;
  font-family: ${designTokens.fonts.body};
  font-size: 0.75rem;
  color: ${designTokens.colors.gray[500]};
  line-height: 1.4;
`;

// ============================================
// Informative tooltip for metric/chart headers
// ============================================

const TooltipWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 5px;
  cursor: default;
  vertical-align: middle;

  &:hover > span {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }
`;

const TooltipIcon = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${designTokens.colors.gray[300]};
  color: white;
  font-size: 9px;
  font-weight: 800;
  font-family: ${designTokens.fonts.body};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  user-select: none;
  flex-shrink: 0;
`;

const TooltipBubble = styled.span`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  background: ${designTokens.colors.gray[900]};
  color: white;
  font-size: 0.72rem;
  font-family: ${designTokens.fonts.body};
  font-weight: 400;
  line-height: 1.45;
  padding: 7px 11px;
  border-radius: 8px;
  white-space: pre-wrap;
  min-width: 200px;
  max-width: 280px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.18);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s;
  z-index: 9999;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: ${designTokens.colors.gray[900]};
  }
`;

export const MetricTooltip = ({ text }) => (
  <TooltipWrap>
    <TooltipIcon>?</TooltipIcon>
    <TooltipBubble>{text}</TooltipBubble>
  </TooltipWrap>
);
